import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getFilterFromEntities } from '@app/utils/entitiesAdapters/getFilterFromEntities'
import { SelectRenderer, RendererWrapper } from '@app/components/renderers'

import { cloneDeep } from '@app/utils/cloneDeep'

import { getContractors } from '@app/api/routes/contractors'

import { SIZE } from '@app/constants'
import { CONTRACTORS_KEY } from '@app/constants/queryKeys'

import type { Column } from '@app/components/base/Table/types'
import type { UseContractorColumnParams } from './types'

export const useContractorEditableColumn = <DataSourceType extends { contractorId: number | null, key: number }>({
  columns,
  patchCreatingTasks
}: UseContractorColumnParams<DataSourceType>): Array<Column<DataSourceType>> => {
  const [columnsWithContractors, setColumnsWithContractors] = useState(columns)

  const { data: contractorsData } = useQuery({
    queryKey: [CONTRACTORS_KEY.contractorsItems],
    queryFn: async () => await getContractors()
  })

  useEffect(() => {
    const contractorColumn = columns.find(column => column.key === 'contractor')

    if (contractorColumn) {
      const options = getFilterFromEntities(contractorsData)

      const handleChange = (contractorId: number | null, record: DataSourceType): void => {
        patchCreatingTasks(contractorId, [record.key])
      }

      contractorColumn.render = (_, record) => (
        <RendererWrapper minWidth={ 200 }>
          <SelectRenderer<DataSourceType>
            record={ record }
            value={ record.contractorId }
            options={ options }
            onChange={ handleChange }
            loading={ !contractorsData }
            disabled={ !contractorsData }
            size={ SIZE.small }
            strictValue
            showSearch
          />
        </RendererWrapper>
      )

      setColumnsWithContractors(cloneDeep(columns))
    }
  }, [contractorsData])

  return columnsWithContractors
}
